import { generateAndDownloadSessionReport } from "@/modules/agent/utils/downloadReport";
import { AGENT_TYPES, UploadDialogState } from "@/modules/agent/types";
import UploadDialog from "@/modules/agent/components/upload-dialog";
import { useGetTeamsListQuery } from "@/modules/auth/requests";
import { useLoggedInMember } from "@/modules/auth/states";
import { frameworkTypeLabels } from "@/modules/sessions/constants";
import { getSessionColumns, getSessionStatusLevel } from "@/modules/sessions/constants/columns";
import { getAgentSession, getPaginatedAgentSessions } from "@/modules/sessions/requests";
import {
  AgentSession,
  AgentSessionStatus,
  AgentSessionStep,
  AgentSessionType,
} from "@/modules/sessions/types";
import {
  handleContinueRiskAssessment,
  handleContinueFairAssessment,
} from "@/modules/sessions/use-cases/agent-session.use-case";
import { ZButton } from "@/shared/components/button";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import ErrorBoundary from "@/shared/components/error-boundary";
import { ZTable } from "@/shared/components/table";
import { ZTablePagination } from "@/shared/components/TablePagination";
import { SearchField } from "@/shared/components/TableSearch";
import { ROUTES } from "@/shared/constants/routes";
import { usePagination } from "@/shared/hooks/usePagination";
import { useSearch } from "@/shared/hooks/useSearch";
import { useSort } from "@/shared/hooks/useSort";
import { addNotification } from "@/shared/states/notification";
import parsePromise from "@/shared/utils/parsePromise";
import {
  Button,
  cn,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Spinner,
} from "@nextui-org/react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Download, Ellipsis } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// Risk assessment session types to filter
const RISK_ASSESSMENT_SESSION_TYPES = [
  AgentSessionType.RISK_ASSESSMENT,
  AgentSessionType.FAIR_ASSESSMENT,
];

export default function RiskAssessmentsPage() {
  const navigate = useNavigate();
  const loggedInMember = useLoggedInMember();
  const memberId = loggedInMember?.member_id;

  // Get teams data for member name resolution
  const { data: teamsData, dataUpdatedAt: teamsDataUpdatedAt } = useGetTeamsListQuery({ isEnabled: true });

  // Session data state
  const [sessionData, setSessionData] = useState<{ sessions: any[] }>({ sessions: [] });

  // Loading states
  const [loadingRows, setLoadingRows] = useState<Record<string, boolean>>({});
  const [isSessionResuming, setIsSessionResuming] = useState<string | null>(null);

  // Modal state
  const [uploadDialogState, setUploadDialogState] = useState<UploadDialogState>({
    isOpen: false,
  });

  // Pagination hook
  const { paginationState, setPaginationState, resetPageNumber } = usePagination();

  // Fetch risk assessment sessions
  const { isFetching: isLoading } = useQuery({
    queryKey: ["risk-assessment-sessions"],
    queryFn: async () => {
      const data = await getPaginatedAgentSessions({
        page_number: 1,
        page_size: 1000, // Fetch all, paginate locally
        filters: {
          session_type: RISK_ASSESSMENT_SESSION_TYPES,
        },
      });

      const sessionsArr = Array.isArray(data?.data?.sessions) ? data.data.sessions : [];
      const sessions = sessionsArr.map((row: AgentSession) => ({
        ...row,
        key: row.id,
        id: row.id,
        status_level: getSessionStatusLevel(row.status),
        created_by_id: row.created_by,
        created_by: teamsData?.memberIdNameMapping?.[row.created_by] || "",
      }));

      setSessionData({ sessions });
      return sessions;
    },
    refetchInterval: 60000,
    refetchOnWindowFocus: false,
  });

  // Update created_by when teams data changes
  useMemo(() => {
    if (teamsDataUpdatedAt && sessionData.sessions.length > 0) {
      setSessionData((prev) => ({
        ...prev,
        sessions: prev.sessions.map((x) => ({
          ...x,
          created_by: teamsData?.memberIdNameMapping?.[x.created_by_id as string] || "",
        })),
      }));
    }
  }, [teamsDataUpdatedAt]);

  // Search hook
  const { searchTerm, setSearchTerm, filteredData: searchFilteredData } = useSearch<any>(
    sessionData.sessions || [],
    {
      searchBy: (session: AgentSession) => {
        const values: string[] = [];
        if (session?.name) values.push(session.name);
        if ((session as any)?.created_by) values.push((session as any).created_by);
        values.push(frameworkTypeLabels[session.type] || "");
        return values;
      },
      resetPageNumber,
    }
  );

  // Sort hook
  const { sortedData, setSortModel } = useSort<any>(
    searchFilteredData || [],
    { column: "", direction: "ascending" },
    { paginationState }
  );

  // Calculate pagination
  const totalSessionsCount = searchFilteredData?.length ?? 0;
  const totalPages = Math.ceil(totalSessionsCount / paginationState.rowsPerPage);

  // Paginate locally
  const paginatedSessions = useMemo(() => {
    const start = (paginationState.currentPage - 1) * paginationState.rowsPerPage;
    const end = paginationState.currentPage * paginationState.rowsPerPage;
    return sortedData?.slice(start, end) ?? [];
  }, [sortedData, paginationState.currentPage, paginationState.rowsPerPage]);

  // Get columns from sessions module
  const columns = useMemo(
    () => getSessionColumns(AgentSessionType.RISK_ASSESSMENT, memberId),
    [memberId]
  );

  // Handle opening the modal
  const handleNewAssessment = () => {
    setUploadDialogState({
      isOpen: true,
      type: AGENT_TYPES.RISK_ASSESSMENT,
    });
  };

  // Handle risk assessment continuation
  const handleRiskAssessment = useCallback(
    async (session: AgentSession, steps: AgentSessionStep[]) => {
      try {
        switch (session.type) {
          case AgentSessionType.RISK_ASSESSMENT:
            await handleContinueRiskAssessment({ session, steps });
            break;
          case AgentSessionType.FAIR_ASSESSMENT:
            await handleContinueFairAssessment({ session, steps });
            break;
          default:
            throw new Error("Invalid session type for risk assessment");
        }
      } catch (error) {
        console.error(error);
      }
    },
    []
  );

  // Handle session continuation
  const handleSessionContinuation = useCallback(
    async (row: any) => {
      if (isLoading || !!isSessionResuming || loadingRows[row.id]) {
        return;
      }

      setLoadingRows((prev) => ({ ...prev, [row.id]: true }));
      setIsSessionResuming(row.id);

      try {
        const response = await getAgentSession(row.id);
        if (!response || !response.data) {
          throw new Error("No session data");
        }

        const { session, steps } = response.data;
        await handleRiskAssessment(session, steps);
        navigate(`/${ROUTES.AGENT}/${row.id}`);
      } catch (error) {
        setLoadingRows((prev) => ({ ...prev, [row.id]: false }));
        setIsSessionResuming(null);
        addNotification({
          type: "error",
          title: "Error",
          message: "Failed to load session details. Please try again.",
        });
        return;
      }

      setLoadingRows((prev) => ({ ...prev, [row.id]: false }));
      setIsSessionResuming(null);
    },
    [navigate, handleRiskAssessment, isLoading, isSessionResuming, loadingRows]
  );

  // Handle download report
  const handleDownloadReport = useCallback(
    async (session: any) => {
      if (isLoading || !!isSessionResuming || loadingRows[session.id]) {
        return;
      }

      setLoadingRows((prev) => ({ ...prev, [session.id]: true }));

      addNotification({
        type: "info",
        title: "Preparing to download",
        message: "Your request to download a report is being processed.",
      });

      const response = await getAgentSession(session.id);
      if (!response || !response.data) {
        setLoadingRows((prev) => ({ ...prev, [session.id]: false }));
        return;
      }

      const { steps } = response.data;
      await parsePromise(generateAndDownloadSessionReport(session.id, undefined, { steps }));

      setLoadingRows((prev) => ({ ...prev, [session.id]: false }));
    },
    [isLoading, isSessionResuming, loadingRows]
  );

  // Handle row click
  const handleRowClick = useCallback(
    async ({ row }: { row: any }) => {
      await handleSessionContinuation(row);
    },
    [handleSessionContinuation]
  );

  // Actions column
  const actionsColumn = useMemo(
    () => ({
      type: "custom" as const,
      fieldName: "actions",
      headerName: "",
      headerClassName: "!w-[60px]",
      renderCell: (row: any) => {
        const { status, id, final_report_available } = row;
        const isRowLoading = loadingRows[id];
        const isReadyForDownload = ![
          AgentSessionStatus.PENDING,
          AgentSessionStatus.QUEUED,
          AgentSessionStatus.IN_PROGRESS,
          AgentSessionStatus.UNDER_REASSESSMENT,
          AgentSessionStatus.FORM_SENT,
          AgentSessionStatus.VENDOR_INPUT_NEEDED,
          AgentSessionStatus.EXTRACT_CONTROLS,
          AgentSessionStatus.REFINE_IN_PROGRESS,
        ].includes(status);

        return (
          <div className="flex justify-end w-full items-center h-full min-h-[40px]">
            {isRowLoading ? (
              <div className="w-8 h-8 flex items-center justify-center">
                <LoadingSpinner variant="centered" className="h-5 w-5" />
              </div>
            ) : (
              <Dropdown>
                <DropdownTrigger>
                  <Button
                    isIconOnly
                    className="bg-transparent hover:bg-gray-100 transition-colors"
                    isDisabled={isLoading || !!isSessionResuming}
                  >
                    <Ellipsis className="h-5 w-5" color="#717179" />
                  </Button>
                </DropdownTrigger>
                <DropdownMenu disabledKeys={!isReadyForDownload || !final_report_available ? ["download"] : []}>
                  <DropdownItem
                    key="continue"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSessionContinuation(row);
                    }}
                    startContent={<ArrowRight className="h-4 w-4 text-[#444444]" />}
                    className="text-[#444444]"
                  >
                    {status === AgentSessionStatus.COMPLETE ? "View Session" : "Continue Session"}
                  </DropdownItem>
                  <DropdownItem
                    key="download"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadReport(row);
                    }}
                    startContent={<Download className="h-4 w-4 text-[#444444]" />}
                    className="text-[#444444]"
                  >
                    Download Report
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            )}
          </div>
        );
      },
    }),
    [loadingRows, isLoading, isSessionResuming, handleSessionContinuation, handleDownloadReport]
  );

  return (
    <ErrorBoundary fallback={null}>
      <div className="w-full h-full flex flex-col">
        {/* Top Bar: Breadcrumb + New Assessment Button */}
        <div className="flex items-center pb-4 justify-between min-h-[44px] max-h-[44px]">
          <div className="flex gap-4 items-center">
            <ZBreadCrumbs
              items={[
                { title: "Internal Risk", href: `/${ROUTES.INTERNAL_RISK_ASSESSMENTS}` },
                { title: "Risk Assessments", href: `/${ROUTES.INTERNAL_RISK_ASSESSMENTS}` },
              ]}
            />
          </div>
          <ZButton
            color="primary"
            className="text-xs font-medium h-7 px-3"
            onClick={handleNewAssessment}
          >
            New Assessment
          </ZButton>
        </div>

        {/* Main Content */}
        <div className="h-full w-full overflow-hidden rounded-lg flex flex-col">
          {/* Header: Title + Count | Search */}
          <div className="px-6 py-4 flex flex-col bg-white gap-2">
            <div className="flex flex-row justify-between items-center gap-2 flex-shrink-0">
              {/* Left: Title + Count */}
              <div className="flex flex-row items-center gap-2 flex-grow">
                <h2 className="font-['Inter'] font-medium text-[14px] leading-[20px] text-[#11181C]">
                  Risk Assessments
                </h2>
                {!isLoading && (
                  <div className="flex flex-row justify-center items-center py-[1px] px-2 gap-1 bg-[#F4F4F5] rounded-[45px]">
                    <span className="font-['Inter'] font-medium text-[10px] leading-[16px] text-[#52525B]">
                      {totalSessionsCount}
                    </span>
                  </div>
                )}
              </div>

              {/* Right: Search */}
              <div className="flex flex-row items-center gap-2">
                <SearchField
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Search by Session Title or Framework"
                  className="w-[420px]"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <ZTable<any>
              className={cn("border-1 border-[#E4E4E7] overflow-auto scroll-auto h-full")}
              id="risk-assessments-table"
              layout="auto"
              loading={isLoading}
              rows={paginatedSessions || []}
              columns={[...columns, actionsColumn]}
              onSortModelChange={setSortModel}
              isHeaderSticky
              isCompact
              removeWrapper
              getRowId={(row) => row.id}
              onRowClick={handleRowClick}
              tableHeaderProps={{
                className: "shadow-none rounded-none",
              }}
              tableBodyProps={{
                emptyContent: isLoading ? (
                  <div className="w-full py-4">
                    <Spinner />
                  </div>
                ) : (
                  "No risk assessments found"
                ),
              }}
              tableRowProps={(row, rowIndex) => ({
                className: cn(
                  rowIndex === 0 ? "border-none" : "border-y",
                  "hover:bg-[#F2F2F2]",
                  "group",
                  "cursor-pointer",
                  row && row.id && loadingRows[row.id] && "cursor-wait"
                ),
              })}
              classNames={{
                wrapper: "!shadow-none !rounded-none",
                base: "!shadow-none !rounded-none",
                th: "!shadow-none !rounded-none !bg-gradient-to-b !from-[#f5f2f4] !to-[rgb(245,242,244)]",
              }}
              aria-label="Risk Assessments"
              isSortingControlled
            />
            <ZTablePagination
              totalPages={totalPages}
              paginationState={paginationState}
              setPagination={setPaginationState}
              className="px-5 !py-3 border-t border-[#E4E4E7]"
            />
          </div>
        </div>
      </div>

      {/* Upload Dialog */}
      <UploadDialog data={uploadDialogState} setIsOpen={setUploadDialogState} />
    </ErrorBoundary>
  );
}
